import { Box, InputBase, useMediaQuery } from "@mui/material";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import { DateRange } from "models/interfaces";
import {
  CalendarGlyph,
  CaretDownGlyph,
  SearchGlyph,
} from "../components/SignRequestsIcons";
import inboxStyles from "./Inbox.styles";
import inboxTokens, {
  inboxResponsiveTokens,
  PHONE_SM_QUERY,
} from "./Inbox.tokens";

const styles = inboxStyles;
const { toolbar } = inboxTokens;
const { phone, tablet } = inboxResponsiveTokens;

/** The magnifier shrinks a step per breakpoint. */
const searchIconSx = {
  width: {
    xs: phone.search.iconSize,
    sm: tablet.toolbar.searchIconSize,
    md: toolbar.searchIconSize,
  },
  height: {
    xs: phone.search.iconSize,
    sm: tablet.toolbar.searchIconSize,
    md: toolbar.searchIconSize,
  },
  color: toolbar.searchIconColor,
  flexShrink: 0,
};

interface InboxToolbarProps {
  searchText: string;
  onSearchChange: (value: string) => void;
  dateRange: DateRange;
  setDateRange: (value: DateRange) => void;
  openDateRangePicker: boolean;
  setOpenDateRangePicker: (value: boolean) => void;
  onDateApplied: Function;
  disabled: boolean;
}

/**
 * Search field plus the "All time" date chip. The date control re-skins the
 * shared `CustomDateRangePicker` through its `sx` / `startIcon` / `endIcon`
 * props rather than duplicating the picker.
 *
 * The reference drops the date chip below `sm` and shortens the search
 * placeholder to "Search inbox" on the narrow phone column — a `placeholder` is
 * an attribute, not a styled node, so that one split needs a media-query hook
 * rather than a breakpoint value.
 */
const InboxToolbar = ({
  searchText,
  onSearchChange,
  dateRange,
  setDateRange,
  openDateRangePicker,
  setOpenDateRangePicker,
  onDateApplied,
  disabled,
}: InboxToolbarProps) => {
  const isNarrowPhone = useMediaQuery(PHONE_SM_QUERY);
  const placeholder = isNarrowPhone
    ? phone.search.placeholderShort
    : toolbar.searchPlaceholder;

  return (
    <Box sx={styles.toolbar}>
      <Box sx={styles.searchField}>
        <SearchGlyph sx={searchIconSx} />
        <InputBase
          id="inbox_search_box"
          type="search"
          placeholder={placeholder}
          value={searchText}
          onChange={(event) => onSearchChange(event.target.value)}
          sx={styles.searchInput}
          inputProps={{ "aria-label": toolbar.searchPlaceholder }}
        />
      </Box>

      <Box sx={styles.dateField}>
        <CustomDateRangePicker
          dateRange={dateRange}
          openDateRangePicker={openDateRangePicker}
          setOpenDateRangePicker={setOpenDateRangePicker}
          setDateRange={setDateRange}
          helperFunction={onDateApplied}
          disabled={disabled}
          isClearable={false}
          autoSizeInput
          sx={styles.datePickerTrigger}
          startIcon={
            <CalendarGlyph
              sx={{
                width: { sm: tablet.toolbar.dateIconSize, md: toolbar.dateIconSize },
                height: {
                  sm: tablet.toolbar.dateIconSize,
                  md: toolbar.dateIconSize,
                },
                color: toolbar.searchIconColor,
                mr: { sm: tablet.toolbar.dateGap, md: toolbar.dateGap },
                flexShrink: 0,
              }}
            />
          }
          endIcon={
            <CaretDownGlyph
              sx={{
                width: toolbar.dateCaretSize,
                height: toolbar.dateCaretSize,
                color: toolbar.searchIconColor,
                ml: { sm: tablet.toolbar.dateGap, md: toolbar.dateGap },
                flexShrink: 0,
              }}
            />
          }
        />
      </Box>
    </Box>
  );
};

export default InboxToolbar;
